// Location: src/features/simulators/shared/utils/dataGenerators.ts
// src/features/simulators/shared/utils/dataGenerators.ts

import type { StepType } from '../types/simulator.types';

// Sample data pools
const firstNames = [
  'John', 'Mary', 'James', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Barbara', 'David', 'Elizabeth', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Nancy', 'Daniel', 'Lisa',
  'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra', 'Donald', 'Ashley',
];

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson',
  'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Thompson', 'White',
  'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young',
];

const paymentMethods = ['Visa', 'Mastercard', 'AmericanExpress', 'Cash'];

const rooms = [
  '101', '102', '103', '104', '105', '106', '107', '108', '109', '110',
  '201', '202', '203', '204', '205', '206', '207', '208', '209', '210',
  '301', '302', '303', '304', '305', '306', '307', '308', '309', '310',
  '401', '402', '403', '404', '405', '406', '407', '408', '409', '410',
  '501', '502', '503', '504', '505', '506', '507', '508', '509', '510',
];

// Utility functions
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function randomPick<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

export function randomBoolean(probability: number = 0.5): boolean {
  return Math.random() < probability;
}

// ID Generators
export function generateUniqueId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

export function generateReservationNumber(prefix: string = 'RES'): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = randomInt(1000, 9999);
  return `${prefix}${timestamp}${random}`;
}

// Generate multiple locators (simulating OTA + PMS)
export function generateAlternateLocators(): string[] {
  const locators: string[] = [];
  
  // OTA locator (like Booking.com)
  if (randomBoolean(0.7)) {
    locators.push(generateReservationNumber('BKNG'));
  }
  
  // Tour operator locator
  if (randomBoolean(0.3)) {
    locators.push(generateReservationNumber('TO'));
  }
  
  return locators;
}

// Guest data generators
export function generateGuestName(): { full: string; masked: string } {
  const firstName = randomPick(firstNames);
  const lastName = randomPick(lastNames);
  const secondLastName = randomPick(lastNames);
  const full = `${firstName} ${lastName} ${secondLastName}`;
  const masked = `${firstName.split(' ')[0]} ${lastName.charAt(0)}.`;
  return { full, masked };
}

export function generateDocument(): { full: string; masked: string } {
  const letter = String.fromCharCode(65 + randomInt(0, 25)); // A-Z
  const numbers = randomInt(10000000, 99999999).toString();
  const full = `${letter}${numbers}`;
  const masked = `${letter}${numbers.substring(0, 4)}***`;
  return { full, masked };
}

export function generateRoomNumber(): string {
  return randomPick(rooms);
}

// Payment generators
export function generatePaymentAmount(min: number = 50, max: number = 300): number {
  return randomInt(min, max);
}

export function generatePaymentMethod(): string {
  return randomPick(paymentMethods);
}

export function generatePaymentOperationId(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = randomInt(100000, 999999);
  return `OP${timestamp}${random}`;
}

// Error generators
export function generateErrorCode(): string {
  const errorCodes = [
    'ERR_PAYMENT_DECLINED',
    'ERR_TIMEOUT',
    'ERR_NETWORK',
    'ERR_VALIDATION',
    'ERR_DEVICE',
    'ERR_CARD_EXPIRED',
    'ERR_INSUFFICIENT_FUNDS',
    'ERR_COMMUNICATION',
  ];
  return randomPick(errorCodes);
}

export function generateDispenserErrorCode(): string {
  const errorCodes = [
    'MECH_JAM',
    'OUT_OF_KEYS',
    'TIMEOUT',
    'SENSOR_ERROR',
    'DOOR_OPEN',
  ];
  return randomPick(errorCodes);
}

// Duration generators
export function generateSectionDuration(step: StepType): number {
  const durations: Record<StepType, [number, number]> = {
    search: [5, 15],
    identification: [10, 30],
    signature: [15, 45],
    upsell: [10, 25],
    payment: [20, 60],
    key: [5, 15],
  };
  
  const [min, max] = durations[step] || [5, 15];
  return randomInt(min, max);
}

export function calculateJourneyDuration(steps: StepType[]): number {
  return steps.reduce((total, step) => {
    return total + generateSectionDuration(step);
  }, 0);
}

// Simulation helpers
export function shouldSimulateError(errorRate: number = 0.05): boolean {
  return randomBoolean(errorRate);
}

export function shouldSimulateAbandonment(abandonmentRate: number = 0.03): boolean {
  return randomBoolean(abandonmentRate);
}

export function selectAbandonmentStep(steps: StepType[]): StepType {
  // More likely to abandon in early steps
  const weights: Record<StepType, number> = {
    search: 0.3,
    identification: 0.25,
    signature: 0.2,
    upsell: 0.15,
    payment: 0.08,
    key: 0.02,
  };
  
  const totalWeight = steps.reduce((sum, step) => sum + (weights[step] || 0), 0);
  let random = Math.random() * totalWeight;
  
  for (const step of steps) {
    random -= weights[step] || 0;
    if (random <= 0) return step;
  }
  
  return steps[0]; // fallback
}

// Format helpers
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

export function formatCurrency(amount: number, currency: string = 'EUR'): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
  }).format(amount);
}

// Delay helper
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}